import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  TextField, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Chip,
  Divider
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faRobot } from '@fortawesome/free-solid-svg-icons';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import MarkdownEditor from './MarkdownEditor';
import MultiSelectDropdown, { MultiSelectOption } from './MultiSelectDropdown';

import { Environment, UpdateEnvironmentRequest, CreateEnvironmentRequest, EnvironmentType } from '../hooks/useEnvironments';
import useGithubSshConfigs from '../hooks/useGithubSshConfigs';
import { useGitHubInstallations } from '../hooks/useGitHubInstallations';
import { useAwsConnections } from '../hooks/useAwsConnections';

import { useParams } from 'react-router-dom';

interface EnvironmentDetailsProps {
  environment: Environment | null;
  loading?: boolean;
  onSave?: (data: UpdateEnvironmentRequest | CreateEnvironmentRequest) => Promise<void>;
  onDelete?: () => Promise<void>;
  editorActions?: any;
  setEditorState?: (state: any) => void;
  isCreateMode?: boolean;
}

const EnvironmentDetails: React.FC<EnvironmentDetailsProps> = ({
  environment,
  loading = false,
  onSave,
  onDelete,
  editorActions,
  setEditorState,
  isCreateMode = false
}) => {
  const { tenantId } = useParams<{ tenantId: string }>();

  const [formData, setFormData] = useState<UpdateEnvironmentRequest & { type?: EnvironmentType }>({
    name: '',
    description: '',
    rules: '',
    specs: '',
    gitConnectionIds: [],
    awsConnectionId: '',
    status: 'active',
    type: 'aws'
  });

  const { data: githubConnections = [] } = useGithubSshConfigs();
  const { data: installations = [] } = useGitHubInstallations();
  const { data: awsConnections = [] } = useAwsConnections();

  useEffect(() => {
    if (environment) {
      setFormData({
        name: environment.name || '',
        description: environment.description || '',
        rules: environment.rules || '',
        specs: environment.specs || '',
        gitConnectionIds: environment.gitConnectionIds || [],
        awsConnectionId: environment.awsConnectionId || '',
        status: environment.status || 'active'
      });
    }
  }, [environment]);



  const handleInputChange = (field: keyof UpdateEnvironmentRequest, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTypeChange = (value: EnvironmentType) => {
    setFormData(prev => ({ ...prev, type: value }));
  };

  const handleSave = useCallback(async () => {
    if (onSave) {
      if (isCreateMode) {
        // For create, include type field
        const createData: CreateEnvironmentRequest = {
          name: formData.name,
          type: formData.type || 'aws',
          description: formData.description,
          rules: formData.rules,
          specs: formData.specs,
          gitConnectionIds: formData.gitConnectionIds,
          awsConnectionId: formData.awsConnectionId
        };
        await onSave(createData);
      } else {
        // For update, exclude type field
        const updateData: UpdateEnvironmentRequest = {
          name: formData.name,
          description: formData.description,
          rules: formData.rules,
          specs: formData.specs,
          gitConnectionIds: formData.gitConnectionIds,
          awsConnectionId: formData.awsConnectionId,
          status: formData.status
        };
        await onSave(updateData);
      }
    }
  }, [formData, onSave, isCreateMode]);

  const handleDelete = useCallback(async () => {
    if (onDelete) {
      await onDelete();
    }
  }, [onDelete]);

  useEffect(() => {
    if (editorActions) {
      editorActions.onSave = handleSave;
      editorActions.onDelete = handleDelete;
    }
  }, [formData, onSave, onDelete, editorActions, handleSave, handleDelete]);

  useEffect(() => {
    if (setEditorState) {
      const isValid = formData.name.trim().length > 0;
      setEditorState({ saveEnabled: isValid });
    }
  }, [formData, setEditorState]);

  const getGitHubConnectionOptions = (): MultiSelectOption[] => {
    const currentTenantId = environment?.tenantId || tenantId;
    const sshOptions = githubConnections
      .filter(conn => conn.tenantId === currentTenantId)
      .map(conn => ({
        id: `${conn.accessMethod}-${conn.tenantId}-${btoa(conn.repositoryUrl + conn.name).replace(/[^a-zA-Z0-9]/g, '')}`,
        label: conn.name || conn.repositoryUrl.split('/').slice(-2).join('/'),
        description: `${conn.accessMethod === 'pat' ? 'PAT' : 'SSH'} - ${conn.repositoryUrl}`,
        icon: conn.accessMethod === 'pat' ? <VpnKeyIcon fontSize="small" /> : <FontAwesomeIcon icon={faKey} />
      }));



    const appOptions = installations
      .map(inst => ({
        id: `app-${inst.connectionId}`,
        label: inst.name,
        description: `GitHub App - ${inst.accountLogin} (${inst.accountType})`,
        icon: <FontAwesomeIcon icon={faRobot} />
      }));

    return [...sshOptions, ...appOptions];
  };

  if (loading) {
    return <Typography>Loading environment details...</Typography>;
  }

  if (!environment && !isCreateMode) {
    return <Typography>Environment not found</Typography>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {isCreateMode ? 'Create New Environment' : 'Environment Details'}
        </Typography>
        {!isCreateMode && environment && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
            <Chip 
              label={environment.type.toUpperCase()} 
              color={environment.type === 'aws' ? 'primary' : 'default'}
              size="small"
            />
            <Chip 
              label={environment.status} 
              color={environment.status === 'active' ? 'success' : 'default'}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              Created: {new Date(environment.createdAt).toLocaleDateString()}
            </Typography>
            {environment.updatedAt && (
              <Typography variant="body2" color="text.secondary">
                Updated: {new Date(environment.updatedAt).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {!isCreateMode && environment?.agentRoleArn && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Agent Role ARN
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {environment.agentRoleArn}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Use this role ARN to configure custom permissions for agent tasks
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <TextField
          label="Name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          fullWidth
          required
        />

        <TextField
          label="Description"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          fullWidth
          multiline
          rows={2}
        />

        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={formData.status}
            label="Status"
            onChange={(e) => handleInputChange('status', e.target.value)}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>

        <MultiSelectDropdown
          label="GitHub Connections"
          options={getGitHubConnectionOptions()}
          selectedIds={formData.gitConnectionIds || []}
          onChange={(ids) => handleInputChange('gitConnectionIds', ids)}
          placeholder="Select GitHub connections..."
          helperText="Choose GitHub connections available to this environment"
        />

        {isCreateMode && (
          <FormControl fullWidth required>
            <InputLabel>Environment Type</InputLabel>
            <Select
              value={formData.type}
              label="Environment Type"
              onChange={(e) => handleTypeChange(e.target.value as EnvironmentType)}
            >
              <MenuItem value="aws">AWS</MenuItem>
              <MenuItem value="terraform" disabled>Terraform (Coming Soon)</MenuItem>
              <MenuItem value="gcp" disabled>Google Cloud Platform (Coming Soon)</MenuItem>
              <MenuItem value="azure" disabled>Microsoft Azure (Coming Soon)</MenuItem>
            </Select>
          </FormControl>
        )}

        {(environment?.type === 'aws' || formData.type === 'aws') && (
          <FormControl fullWidth>
            <InputLabel>AWS Connection</InputLabel>
            <Select
              value={formData.awsConnectionId || ''}
              label="AWS Connection"
              onChange={(e) => handleInputChange('awsConnectionId', e.target.value)}
            >
              {awsConnections
                .filter((conn: any) => conn.tenantId === (environment?.tenantId || tenantId) && conn.isActive)
                .map((conn: any) => (
                  <MenuItem key={conn.id} value={conn.id}>
                    {conn.name} ({conn.accountId} - {conn.region}) {conn.isActive ? '✓' : '✗'}
                  </MenuItem>
                ))
              }
            </Select>
            {awsConnections.filter((conn: any) => conn.tenantId === (environment?.tenantId || tenantId) && conn.isActive).length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                No active AWS connections available
              </Typography>
            )}
          </FormControl>
        )}

        <Divider />

        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Rules
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Define deployment rules and constraints in Markdown format
          </Typography>
          <MarkdownEditor
            value={formData.rules || ''}
            onChange={(value) => handleInputChange('rules', value)}
            placeholder="Enter deployment rules..."
            height={200}
          />
        </Box>

        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Specifications
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Define technical specifications and requirements in Markdown format
          </Typography>
          <MarkdownEditor
            value={formData.specs || ''}
            onChange={(value) => handleInputChange('specs', value)}
            placeholder="Enter technical specifications..."
            height={200}
          />
        </Box>
      </Box>

    </Box>
  );
};

export default EnvironmentDetails;